import type { ReactNode } from 'react';
import { Link } from 'react-router-dom';
import { useAuth } from '../../auth/AuthProvider';
import { Avatar } from '../ui/Avatar';
import { useShellRendersChrome } from './chrome-context';
import { ThemeToggle } from './ThemeToggle';

/**
 * The top bar, now with two slots the shell fills.
 *
 * ⭐ It stays presentational: `AppShell` decides WHAT goes in `nav` and `switcher`, this file only
 * decides where they sit and how the row survives 390px. Identity, theme and sign-out are unchanged.
 *
 * 🔴 The 390px budget is real and was measured, not estimated: mark 20 + switcher ≤120 + theme
 * toggle 104 + Sign out ~68 + gaps ≈ 330 of the 366 usable pixels. The "PlanHub" wordmark is the one
 * thing in here that carries no state and no action, so it is what gives way below `sm` — the
 * coloured mark still identifies the app, and everything that DOES something stays reachable.
 */
export function TopBar({ nav, switcher }: { nav?: ReactNode; switcher?: ReactNode }) {
  const { state, signOut } = useAuth();
  const alreadyDrawn = useShellRendersChrome();
  const user = state.status === 'authenticated' ? state.user : null;

  /**
   * ⭐ A view inside the shell that renders its own `<TopBar/>` gets nothing, instead of a second
   * header stacked under the first. See `chrome-context.ts` — the shell owns the chrome.
   */
  if (alreadyDrawn) return null;

  return (
    <header className="flex h-12 shrink-0 items-center gap-2 border-b border-[color:var(--color-line)] bg-[color:var(--color-surface)] px-3 sm:px-4">
      <span className="flex shrink-0 items-center gap-2">
        <span
          aria-hidden="true"
          className="inline-block size-5 rounded bg-[color:var(--color-accent)]"
        />
        <span className="hidden text-sm font-bold tracking-tight sm:inline">PlanHub</span>
      </span>

      {/* Mobile only — the desktop equivalent is the persistent rail on the left. */}
      {switcher}

      {/* Desktop only — the mobile equivalent is the bottom tab bar. */}
      {nav ? <div className="ml-1 hidden min-w-0 md:block">{nav}</div> : null}

      <div className="ml-auto flex shrink-0 items-center gap-2 sm:gap-3">
        {/**
         * ⛔ RENDERED ONLY FOR ADMINS, AND THAT IS NOT THE ACCESS CONTROL. `/admin` re-checks with
         * `RequireAdmin`, and every endpoint behind it re-checks with the API's own `requireAdmin` —
         * three independent checks, because hiding a link stops nobody who can type a URL.
         * ⭐ It is here rather than in `views.ts` on purpose: that list drives the desktop tab row AND
         * the 390px bottom bar, both of which EVERY signed-in user sees. A tab most people are
         * refused is worse than no tab.
         *
         * ⚠️ ICON-ONLY, at 32px, because the 390px budget in the note above has ~36px of slack and a
         * text label would not fit. Measured at 390 with the row rendered, not estimated.
         */}
        {user?.is_admin ? (
          <Link
            to="/admin"
            title="Administration"
            aria-label="Administration"
            className="flex size-8 shrink-0 items-center justify-center rounded border border-[color:var(--color-line)] text-sm hover:bg-black/5 dark:hover:bg-white/10"
          >
            <span aria-hidden="true">⚙</span>
          </Link>
        ) : null}
        <ThemeToggle />
        {user ? (
          <>
            <span className="hidden items-center gap-1.5 sm:flex">
              {/* ⚠️ The API's SafeUser carries no avatar URL, so this is always initials. Deliberate:
                  proxying GitLab avatars would mean holding a token in the browser. */}
              <Avatar name={user.display_name || user.gitlab_username} size={24} />
              <span className="max-w-[10rem] truncate text-sm">
                {user.display_name || user.gitlab_username || user.email}
              </span>
              {user.user_type === 'ai_agent' ? (
                <span className="rounded bg-purple-100 px-1 text-[10px] font-bold text-purple-800 uppercase dark:bg-purple-900/60 dark:text-purple-200">
                  agent
                </span>
              ) : null}
            </span>
            <button
              type="button"
              onClick={() => void signOut()}
              className="h-8 rounded border border-[color:var(--color-line)] px-2.5 text-sm font-medium hover:bg-black/5 dark:hover:bg-white/10"
            >
              Sign out
            </button>
          </>
        ) : null}
      </div>
    </header>
  );
}
